/**
 * ExternalPaymentOnboardingPage
 * Claim-per-tenant interface after linking bank account
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { OnboardingTenant } from '../api/external-payments.api';
import { fetchOnboardingData, submitOnboardingMatches } from '../api/external-payments.api';
import { getPropertyBankAccount } from '../lib/api';
import { OnboardingTenantCard } from '../components/OnboardingTenantCard';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { HelpBlock } from '../components/ui/HelpTooltip';

export function ExternalPaymentOnboardingPage(): JSX.Element {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();

  const [tenants, setTenants] = useState<OnboardingTenant[]>([]);
  const [linkedBankAccountId, setLinkedBankAccountId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Track selections: tenantId -> Set of selected transaction IDs
  const [selections, setSelections] = useState<Map<string, Set<string>>>(new Map());
  // Track skipped tenants
  const [skippedTenants, setSkippedTenants] = useState<Set<string>>(new Set());

  const loadData = useCallback(async () => {
    if (!propertyId) return;
    try {
      setIsLoading(true);
      setError(null);
      const [onboardingData, bankAccountData] = await Promise.all([
        fetchOnboardingData(propertyId),
        getPropertyBankAccount(propertyId),
      ]);
      setTenants(onboardingData.tenants);
      setLinkedBankAccountId(bankAccountData?.linkedBankAccountId ?? null);

      // Initialize empty selection sets
      const initSelections = new Map<string, Set<string>>();
      onboardingData.tenants.forEach((t: OnboardingTenant) => {
        initSelections.set(t.tenantId, new Set());
      });
      setSelections(initSelections);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load onboarding data');
    } finally {
      setIsLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleTransactionToggle = (tenantId: string, txId: string) => {
    setSelections((prev) => {
      const next = new Map(prev);
      const current = new Set(next.get(tenantId) ?? []);
      if (current.has(txId)) {
        current.delete(txId);
      } else {
        current.add(txId);
      }
      next.set(tenantId, current);
      return next;
    });
  };

  const handleSkipTenant = (tenantId: string) => {
    setSkippedTenants((prev) => {
      const next = new Set(prev);
      if (next.has(tenantId)) {
        next.delete(tenantId);
      } else {
        next.add(tenantId);
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!propertyId || !linkedBankAccountId) return;

    try {
      setIsSubmitting(true);
      setSubmitError(null);

      const matches: { bankTransactionId: string; tenantId: string; leaseId: string }[] = [];
      tenants.forEach((tenant) => {
        if (skippedTenants.has(tenant.tenantId)) return;
        const selectedTxIds = selections.get(tenant.tenantId) ?? new Set();
        selectedTxIds.forEach((txId) => {
          matches.push({
            bankTransactionId: txId,
            tenantId: tenant.tenantId,
            leaseId: tenant.leaseId,
          });
        });
      });

      await submitOnboardingMatches(propertyId, {
        linkedBankAccountId,
        matches,
      });

      navigate(`/properties/${propertyId}/external-payments/transactions`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit matches');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div data-testid="onboarding-page" className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)]">
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="space-y-2 mb-6">
              <Skeleton className="h-8 w-72" />
              <Skeleton className="h-4 w-96" />
            </div>
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-40 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="onboarding-page"
      className="max-w-4xl mx-auto pb-6"
    >
      <div className="px-[var(--page-padding-x)]">
        <div className="max-w-3xl mx-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-foreground m-0">
              Set Up External Payment Detection
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Select recent transactions that match each tenant's rent payment to set up automatic
              detection.
            </p>
          </div>

          <div className="mb-6">
            <HelpBlock
              summary="Help the system learn which bank deposits belong to each tenant. Select recent transactions below that you know are rent payments — the system will use these to automatically recognize future payments from the same tenant."
              details={"Why this matters: Each tenant's payments may appear with different descriptions (e.g., their name, a Zelle ID, or a Cash App handle). By selecting a few examples, you're teaching the system what to look for.\n\nYou can skip tenants you're unsure about — the system will still try to match them based on name, and you can confirm matches later from the External Payments hub.\n\nAfter saving, the lease enters a Learning phase where the system watches for patterns without applying payments automatically. Once it's proven reliable, it will promote to Active mode."}
            />
          </div>

          {error && (
            <div className="px-4 py-3 bg-destructive/5 border border-destructive/20 rounded-lg mb-6">
              <p className="m-0 text-sm text-destructive">{error}</p>
            </div>
          )}

          {!linkedBankAccountId && !error && (
            <div className="px-4 py-3 bg-accent/50 border border-border rounded-lg mb-6">
              <p className="m-0 text-sm text-muted-foreground">
                No bank account is linked for this property. Please configure a linked bank account
                first.
              </p>
            </div>
          )}

          {tenants.length === 0 && !error && (
            <div className="text-center py-16 px-6 bg-card rounded-lg border border-border">
              <p className="text-sm text-muted-foreground">
                No tenants with active leases found for this property.
              </p>
            </div>
          )}

          {tenants.length > 0 && (
            <form onSubmit={handleSubmit}>
              {tenants.map((tenant) => (
                <OnboardingTenantCard
                  key={tenant.tenantId}
                  tenant={tenant}
                  selectedTransactionIds={selections.get(tenant.tenantId) ?? new Set()}
                  skipped={skippedTenants.has(tenant.tenantId)}
                  onTransactionToggle={(txId) => handleTransactionToggle(tenant.tenantId, txId)}
                  onSkip={() => handleSkipTenant(tenant.tenantId)}
                />
              ))}

              {submitError && (
                <div className="px-4 py-3 bg-destructive/5 border border-destructive/20 rounded-lg mb-4">
                  <p className="m-0 text-sm text-destructive">{submitError}</p>
                </div>
              )}

              <div className="flex justify-end mt-4">
                <Button
                  type="submit"
                  data-testid="submit-onboarding-button"
                  disabled={isSubmitting || !linkedBankAccountId}
                  className="min-h-[44px]"
                >
                  {isSubmitting ? 'Saving...' : 'Save Matches'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExternalPaymentOnboardingPage;
